interface OroMarkProps {
  /** Rendered width/height in px (square). Defaults to 32. */
  size?: number;
  className?: string;
  /**
   * When the mark sits next to a visible "ORO move" wordmark, render it decorative
   * (`aria-hidden`) so screen readers don't announce the name twice.
   */
  decorative?: boolean;
}

/** One dot of the brand mark, in viewBox units + fixed artwork color. */
export interface OroMarkDot {
  color: string;
  cx: number;
  cy: number;
  r: number;
}

/** The square viewBox the dot geometry is expressed in. */
export const ORO_MARK_VIEWBOX = 100;

/**
 * Canonical brand-mark dot geometry — blue → green → gold, ascending.
 * Exported so derived renderings (`OroLoader`, #2675) animate the SAME artwork
 * instead of re-declaring approximate copies. Mirrors
 * `mobile/src/components/OroMark.tsx`'s `ORO_MARK_DOTS` (#2547: OroMark stays as
 * the geometry mirror).
 */
// eslint-disable-next-line react-refresh/only-export-components -- geometry lives with the mark (#2547 mirror); parity with mobile OroMark.tsx
export const ORO_MARK_DOTS: readonly OroMarkDot[] = [
  { color: '#5B8DBE', cx: 32.7, cy: 71.5, r: 7 },
  { color: '#5F9C63', cx: 42, cy: 54.3, r: 9 },
  { color: '#D9A94F', cx: 61.5, cy: 40.5, r: 11.5 },
];

/**
 * ORO move brand mark — three ascending dots (blue → green → gold).
 *
 * Canonical geometry lives in this component (mirrored in `mobile/src/components/OroMark.tsx`);
 * the raster/vector masters are tracked in `brand/source/` and all derived icons are
 * regenerated by `scripts/generate-brand-assets.mjs`.
 *
 * The fill colors are the fixed brand-mark palette (artwork, not theme tokens), so they are
 * intentionally literal hex rather than Layer-2 CSS tokens — the same way an `<img>` logo
 * carries its own colors.
 */
export function OroMark({ size = 32, className, decorative = false }: OroMarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${ORO_MARK_VIEWBOX} ${ORO_MARK_VIEWBOX}`}
      className={className}
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': 'ORO move' })}
    >
      {ORO_MARK_DOTS.map((dot) => (
        <circle key={dot.color} cx={dot.cx} cy={dot.cy} r={dot.r} fill={dot.color} />
      ))}
    </svg>
  );
}
